import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkflows, useCreateWorkflow, useDeleteWorkflow } from '@/hooks/useDocFlow';
import PartCreateFlowPreview from './PartCreateFlowPreview';

const WorkflowListPage: React.FC = () => {
  const { t } = useTranslation('docflow');
  const navigate = useNavigate();
  const { data: workflows, isLoading, error } = useWorkflows();
  const createWorkflow = useCreateWorkflow();
  const deleteWorkflow = useDeleteWorkflow();

  const handleCreate = useCallback(() => {
    createWorkflow.mutate(
      { name: 'New Workflow', description: '' },
      {
        onSuccess: (wf) => {
          navigate(`/app/docflow/${wf.id}/edit`);
        },
      }
    );
  }, [createWorkflow, navigate]);

  const handleDelete = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t('workflow.deleteConfirm'))) {
      deleteWorkflow.mutate(id);
    }
  }, [deleteWorkflow, t]);

  const [showPartCreatePreview, setShowPartCreatePreview] = useState(false);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('subtitle')}
          </p>
        </div>
        <button
          onClick={handleCreate}
          disabled={createWorkflow.isLoading}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {createWorkflow.isLoading ? '...' : t('workflow.create')}
        </button>
      </div>

      {/* Preset Flows */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Preset Flows
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            onClick={() => setShowPartCreatePreview(true)}
            className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100">Part Create Flow</h3>
              </div>
              <span className="text-xs px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-full">
                Preset
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Scan PDF/image documents and extract part code fields via Claude Vision. Supports region selection for targeted extraction.
            </p>
            <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                6 steps
              </span>
              <span>PDF → Extract → Review → Save</span>
            </div>
          </div>
        </div>
      </div>

      {/* Part Create Flow Preview Dialog */}
      {showPartCreatePreview && (
        <PartCreateFlowPreview onClose={() => setShowPartCreatePreview(false)} />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-400">Loading...</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-red-500 text-center py-8">
          {t('error.loading')}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && workflows?.length === 0 && (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          <p className="text-lg mb-2">{t('workflow.empty')}</p>
          <button
            onClick={handleCreate}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            {t('workflow.create')}
          </button>
        </div>
      )}

      {/* Workflow grid */}
      {workflows && workflows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workflows.map((wf) => (
            <div
              key={wf.id}
              onClick={() => navigate(`/app/docflow/${wf.id}/edit`)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {wf.name}
                </h3>
                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/docflow/${wf.id}/run`);
                    }}
                    className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800"
                  >
                    {t('workflow.run')}
                  </button>
                  <button
                    onClick={(e) => handleDelete(wf.id, e)}
                    className="text-xs px-2 py-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {wf.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                  {wf.description}
                </p>
              )}

              <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
                <span>{wf.nodes?.length || 0} {t('workflow.nodes').toLowerCase()}</span>
                <span>{new Date(wf.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowListPage;
